import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../UI/Modal/Modal';
import tbl from '../../../assets/Style/Table.module.css';
import cl from './CurrenciesList.module.css';
import { AppContext } from '../../../providers/appProvider';
import { useCurrency } from '../../../hooks/useCurrency';

const emptyCurrency = {
  name: '',
  symbol: '',
  rate: '',
};

const CurrenciesList = () => {
  const { t } = useTranslation();
  const { app } = useContext(AppContext);
  const { currencyService } = useCurrency();
  const [formData, setFormData] = useState(emptyCurrency);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    currencyService.fetch().catch(() => {
      app.setError(t('CurrencyLoadError'));
    });
    // The list is loaded once when the protected page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (currency) => {
    setFormData({
      name: currency.name || '',
      symbol: currency.symbol || '',
      rate: currency.rate ?? '',
    });
    setValidationError('');
    setIsModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const save = async () => {
    const rate = Number(formData.rate);
    if (!formData.name.trim() || !Number.isFinite(rate) || rate <= 0) {
      setValidationError(t('CurrencyValidationError'));
      return;
    }

    try {
      await currencyService.update({
        ...formData,
        name: formData.name.trim(),
        rate,
      });
      setIsModalOpen(false);
      app.setOk(t('CurrencyUpdated'));
    } catch (error) {
      app.setError(error.response?.data?.message || t('CurrencyUpdateError'));
    }
  };

  return (
    <div className={cl.wrapper}>
      <div className={cl.header}>
        <h1>{t('Currencies')}</h1>
      </div>

      <div className={tbl.table}>
        <table>
          <thead>
            <tr>
              <th>{t('Name')}</th>
              <th>{t('Symbol')}</th>
              <th>{t('Rate')}</th>
              {app.hasPermission('CURRENCY_EDIT') && <th>{t('Actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {currencyService.items.map((currency) => (
              <tr key={currency.symbol}>
                <td>{currency.name}</td>
                <td>{currency.symbol}</td>
                <td>{Number(currency.rate).toFixed(4)}</td>
                {app.hasPermission('CURRENCY_EDIT') && (
                  <td>
                    <button className={cl.linkBtn} onClick={() => openEdit(currency)}>
                      {t('Edit')}
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {!currencyService.isLoading && currencyService.items.length === 0 && (
              <tr>
                <td colSpan={app.hasPermission('CURRENCY_EDIT') ? 4 : 3} className={cl.empty}>
                  {t('NoCurrenciesYet')}
                </td>
              </tr>
            )}
            {currencyService.isLoading && (
              <tr>
                <td colSpan={app.hasPermission('CURRENCY_EDIT') ? 4 : 3} className={cl.empty}>
                  {t('Loading')}...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal size="small" isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className={cl.modalHead}>
          <h3>{t('EditCurrency')}</h3>
        </div>

        <div className={cl.form}>
          <label className={cl.field}>
            <span>{t('Name')}</span>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              autoComplete="off"
            />
          </label>

          <label className={cl.field}>
            <span>{t('Symbol')}</span>
            <input type="text" value={formData.symbol} readOnly disabled />
          </label>

          <label className={cl.field}>
            <span>{t('Rate')}</span>
            <input
              type="number"
              min="0.0001"
              step="0.0001"
              name="rate"
              value={formData.rate}
              onChange={handleChange}
              readOnly={formData.symbol === '$'}
              disabled={formData.symbol === '$'}
            />
          </label>

          {formData.symbol === '₾' && (
            <div className={cl.hint}>{t('CurrencySyncHint')}</div>
          )}
          {validationError && <div className={cl.error}>{validationError}</div>}
        </div>

        <div className={cl.modalActions}>
          <button className={cl.secondaryBtn} onClick={() => setIsModalOpen(false)}>
            {t('Cancel')}
          </button>
          <button className={cl.primaryBtn} onClick={save}>
            {t('Update')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CurrenciesList;
